/**
 * Capacitor native PDF save/share. Plugins are loaded with dynamic import so
 * web builds still work when the packages are not present.
 */

export async function isCapacitorNative(): Promise<boolean> {
    try {
        const { Capacitor } = await import('@capacitor/core');
        return !!Capacitor?.isNativePlatform?.();
    } catch {
        return false;
    }
}

function sanitizeFileName(name: string): string {
    const trimmed = (name || 'invoice.pdf').trim() || 'invoice.pdf';
    return trimmed.replace(/[/\\?%*:|"<>]/g, '-');
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read PDF'));
        reader.readAsDataURL(blob);
    });
}

/**
 * Write a PDF blob to Directory.Cache. Shares immediately unless deferShare is set
 * (bulk downloads write every file first, then share all URIs once).
 * Returns the native file URI, or undefined when not running in a Capacitor app.
 */
export async function savePdfBlobOnNative(
    blob: Blob,
    fileName: string,
    options?: { deferShare?: boolean }
): Promise<string | undefined> {
    if (!(await isCapacitorNative())) {
        return undefined;
    }

    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const safeName = sanitizeFileName(fileName);
    const base64 = await blobToBase64(blob);
    const written = await Filesystem.writeFile({
        path: safeName,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
    });

    let uri = written?.uri;
    if (!uri) {
        const located = await Filesystem.getUri({
            path: safeName,
            directory: Directory.Cache,
        });
        uri = located?.uri;
    }
    if (!uri) {
        throw new Error('Failed to write invoice PDF on device');
    }

    if (!options?.deferShare) {
        await shareNativePdfFiles([uri], safeName);
    }

    return uri;
}

/** Share previously written native file URIs. Returns false if the share sheet fails (e.g. iOS gesture timeout). */
export async function shareNativePdfFiles(uris: string[], title?: string): Promise<boolean> {
    const files = (uris || []).filter(Boolean);
    if (!files.length) {
        return false;
    }
    try {
        const { Share } = await import('@capacitor/share');
        const payload: {
            title: string;
            dialogTitle: string;
            files: string[];
            url?: string;
        } = {
            title: title || (files.length === 1 ? 'Invoice' : 'Invoices'),
            dialogTitle: title || 'Save invoice',
            files,
        };
        if (files.length === 1) {
            payload.url = files[0];
        }
        await Share.share(payload);
        return true;
    } catch (e) {
        console.warn('Native PDF share failed', e);
        return false;
    }
}
